import React from 'react';
import styled from 'styled-components';

const MAX_PASSWORD_LEN = 16;

const EnvelopeGateEl = styled.div`
  position: relative;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  box-sizing: border-box;
  aspect-ratio: 4 / 3;
  background:
    linear-gradient(to bottom, transparent 99px, rgba(0, 0, 0, 0.07) 100px, transparent 101px),
    linear-gradient(145deg, #f5f0e8 0%, #e8e0d4 50%, #ddd4c8 100%);
  border: 1px solid rgba(120, 100, 80, 0.35);
  border-radius: 4px;
  box-shadow:
    inset 0 2px 4px rgba(255, 255, 255, 0.5),
    0 4px 12px rgba(0, 0, 0, 0.2),
    0 1px 0 rgba(255, 255, 255, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 32px 40px;
  @media (max-width: 768px) {
    padding: 32px 20px 28px;
  }
  @media (max-width: 480px) {
    padding: 24px 14px 20px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    border-left: min(280px, 50%) solid transparent;
    border-right: min(280px, 50%) solid transparent;
    border-top: 100px solid transparent;
    pointer-events: none;
    filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.08));
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(180deg, rgba(235, 228, 218, 0.98) 0%, rgba(224, 216, 204, 0.95) 70%, rgba(218, 208, 196, 0.92) 100%);
    clip-path: polygon(50% 100%, 0 0, 100% 0);
    pointer-events: none;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -2px 4px rgba(0, 0, 0, 0.06);
  }
`;

const SealWrapper = styled.form`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const Seal = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #8b3a3a, #5c2020 60%, #4a1818);
  box-shadow:
    inset 2px 2px 8px rgba(255, 255, 255, 0.15),
    inset -2px -2px 6px rgba(0, 0, 0, 0.4),
    0 4px 12px rgba(0, 0, 0, 0.35);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  cursor: pointer;
  transition: transform 0.15s ease;
  &:hover {
    transform: scale(1.04);
  }
`;

const SealFalling = styled(Seal)`
  animation: sealFall 0.6s ease-in forwards;
  pointer-events: none;
  @keyframes sealFall {
    0% {
      transform: translateY(0) rotate(0deg);
      opacity: 1;
    }
    100% {
      transform: translateY(120px) rotate(12deg);
      opacity: 0;
    }
  }
`;

const EnvelopeGateOpening = styled(EnvelopeGateEl)`
  animation: envelopeFadeOut 0.4s ease-out forwards;
  pointer-events: none;
  @keyframes envelopeFadeOut {
    to {
      opacity: 0;
      transform: scale(0.98);
    }
  }
`;

const SealInputWrapper = styled.div`
  position: relative;
  width: 60px;
`;

const SealInput = styled.input`
  width: 100%;
  max-width: 50px;
  padding: 6px 6px;
  border: none;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.25);
  color: #f5e6e6;
  font-size: 0.9rem;
  text-align: center;
  outline: none;
  @media (max-width: 768px) {
    font-size: 16px;
  }

  &::placeholder {
    color: rgba(245, 230, 230, 0.4);
  }
  &:focus {
    background: rgba(0, 0, 0, 0.35);
    box-shadow: 0 0 0 2px rgba(200, 160, 160, 0.4);
  }
`;

const SealCounter = styled.span`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-65%);
  font-size: 0.9rem;
  font-weight: 700;
  color: #fbeaea;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
  pointer-events: none;
`;

const ErrorText = styled.p`
  margin: 6px 0 0;
  color: #c44;
  font-size: 0.9rem;
  font-weight: 600;
`;

const PasswordHint = styled.p`
  margin: 4px 0 0;
  color: rgba(92, 82, 72, 0.92);
  font-size: 0.8rem;
  line-height: 1.45;
  text-align: center;
  max-width: 360px;
`;

const UnderlineText = styled.span`
  display: inline-block;
  padding: 0 0.02em 0.08em;
  border-bottom: 1.5px solid rgba(92, 82, 72, 0.75);
  line-height: 1.2;
`;

export function EnvelopeGate({
  formRef,
  password,
  setPassword,
  error,
  onSubmit,
  openingPhase,
}) {
  const remaining = Math.max(0, MAX_PASSWORD_LEN - (password?.length || 0));

  if (openingPhase === 'envelope-open') {
    return (
      <EnvelopeGateOpening aria-hidden>
      </EnvelopeGateOpening>
    );
  }
  if (openingPhase === 'seal-fall') {
    return (
      <EnvelopeGateEl aria-hidden>
        <SealWrapper as="div">
          <SealFalling />
        </SealWrapper>
      </EnvelopeGateEl>
    );
  }
  return (
    <EnvelopeGateEl>
      <SealWrapper ref={formRef} onSubmit={onSubmit}>
        <input type="hidden" name="username" autoComplete="username" value="charley" />
        <Seal
          onClick={(e) => {
            if (!e.target.closest('input')) formRef.current?.requestSubmit();
          }}
          title="按封蠟開啟"
        >
          <SealCounter aria-hidden="true">{remaining}</SealCounter>
          <SealInputWrapper>
            <SealInput
              id="charley-password"
              name="charley-password"
              type="password"
              placeholder=""
              autoComplete="current-password"
              value={password}
              maxLength={MAX_PASSWORD_LEN}
              onChange={(e) => setPassword(e.target.value.slice(0, MAX_PASSWORD_LEN))}
              aria-label="密碼"
            />
          </SealInputWrapper>
        </Seal>
        {error && <ErrorText>{error}</ErrorText>}
        <PasswordHint>
          密碼提示：<br />
          {'第一次約會的日期查理生日大祐生日'.split('').map((char, i) => (
            <React.Fragment key={i}>
              <UnderlineText>{char}</UnderlineText>{' '}
            </React.Fragment>
          ))}
        </PasswordHint>
      </SealWrapper>
    </EnvelopeGateEl>
  );
}
